import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { fromNow } from "@/lib/format";
import type { Notification } from "@shared/types";

const ICONS: Record<string, string> = {
  "task.assigned": "📋", "activity.status": "🔄", "evidence.added": "📎",
  "approval.requested": "✅", "approval.decided": "⚖️", "deadline.soon": "⏰",
  "deadline.overdue": "🔴", "project.added": "📁", system: "🔔",
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["notifications", "peek"],
    queryFn: () => api.get<{ notifications: Notification[]; unreadCount: number }>("/notifications?limit=12"),
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) => api.post("/notifications/read", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = data?.unreadCount ?? 0;

  return (
    <div className="relative" ref={ref}>
      <button className="btn-ghost relative !p-2" onClick={() => setOpen((o) => !o)} aria-label="Notifications">
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-st-delayed px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-[22rem] animate-fade-in rounded-xl border border-line bg-surface shadow-pop">
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <span className="text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <button className="flex items-center gap-1 text-xs text-brand-600 hover:underline" onClick={() => markRead.mutate({ all: true })}>
                <CheckCheck size={13} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {(data?.notifications ?? []).length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-ink-faint">You're all caught up.</p>
            )}
            {(data?.notifications ?? []).map((n) => (
              <Link
                key={n.id}
                to={n.link || "/notifications"}
                onClick={() => { if (!n.isRead) markRead.mutate({ ids: [n.id] }); setOpen(false); }}
                className={`flex gap-3 border-b border-line px-4 py-3 last:border-0 hover:bg-surface-2 ${n.isRead ? "opacity-60" : ""}`}
              >
                <span className="text-lg leading-none">{ICONS[n.type] || "🔔"}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{n.title}</span>
                  {n.body && <span className="mt-0.5 block line-clamp-2 text-xs text-ink-soft">{n.body}</span>}
                  <span className="mt-0.5 block text-[11px] text-ink-faint">{fromNow(n.createdAt)}</span>
                </span>
                {!n.isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />}
              </Link>
            ))}
          </div>
          <Link to="/notifications" onClick={() => setOpen(false)} className="block border-t border-line px-4 py-2.5 text-center text-xs font-medium text-brand-600 hover:bg-surface-2">
            View all
          </Link>
        </div>
      )}
    </div>
  );
}
